<template>
  <DBForm :store="store" :config="settings" :data="data" ref="form">
    <template v-slot="form">
      <Card :panelFun="controller.panelFun.value" :panel="panel.key">
        <div class="card mb-2">
          <div class="card-header bg-light py-1">
            <h6 class="mb-0">
              <i class="icon icon-file-text"></i>
              Накладная-требование
            </h6>
          </div>
          <div class="card-body py-2">
            <div class="form-row">
              <div class="col-lg-3 my-input-1">
                <DBEdit :form="form" field="requirement_number" readonly />
              </div>
              <div class="col-lg-3 date-input bold-label">
                <DBEdit :form="form" field="approval_date" />
              </div>
              <div class="col-lg-6 mt-3">
                <button
                  v-if="form.data?.status === 'requested' || form.data?.status === 'approved'"
                  @click.prevent="issueRequirement"
                  class="btn btn-primary"
                  title="Отпустить товары"
                >
                  <i class="icon icon-check"></i>
                  Отпустить
                </button>
              </div>
            </div>
            <div class="form-row">
              <div class="col-lg-6 bold-label">
                <DBEdit :form="form" field="requested_by_id" />
              </div>
              <div class="col-lg-6">
                <DBEdit :form="form" field="requested_mol_fio" readonly />
              </div>
            </div>
            <div class="form-row">
              <div class="col-lg-6">
                <DBEdit :form="form" field="issued_by_id" />
              </div>
              <div class="col-lg-6">
                <DBEdit :form="form" field="issued_mol_fio" readonly />
              </div>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header bg-light py-1">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="mb-0">
                <i class="icon icon-list"></i>
                Наименование ТМЦ
                <span class="badge badge-success ml-2"
                  >Позиций: {{ itemsCount }}</span
                >
              </h6>
            </div>
          </div>
          <div class="card-body py-2">
            <DBGrid
              :form="form"
              field="items"
              :config="itemsGridConfig"
              :owner="form.data?.id"
              class="invoice-requirement-items-grid"
              ref="itemsGrid"
            ></DBGrid>
          </div>
        </div>
      </Card>
    </template>
  </DBForm>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  ref,
  watch,
  onMounted,
  nextTick,
} from "vue";
import InvoiceRequirementsEditController from "./EditController";
import Swal from "sweetalert2";
import { edit } from "@/core/db";
import api from "@/core/api";

export default defineComponent({
  props: {
    panel: { type: Object, default: () => ({}) },
    store: { type: Object, default: () => ({}) },
    config: { type: Object, default: () => ({}) },
    data: { type: Object, default: () => ({}) },
  },
  setup(props) {
    const form = ref(null);
    const itemsGrid = ref(null);
    const controller = new InvoiceRequirementsEditController(form, props);

    const formatNumber = (num: number) => {
      const str = num.toFixed(3);
      return str.replace(/\.?0+$/, "");
    };

    const loadStockBalances = async (items: any[]) => {
      for (const item of items) {
        if (item.medicament_stock_id && !item._stock_balance) {
          try {
            const availableResponse: any = await api.query(
              "/nsi/medicament-stock/" + item.medicament_stock_id + "/available-quantity",
              { method: "POST", data: item.id ? { excludeItemId: item.id } : {} },
            );
            if (availableResponse?.success) {
              const availablePkg = parseFloat(availableResponse.availablePackages) || 0;
              const packageCount = parseFloat(availableResponse.packageCount) || 1;
              const availableUnits = Math.round(availablePkg * packageCount * 1000) / 1000;
              item._stock_balance = `Доступно: ${formatNumber(availablePkg)} упак (${formatNumber(availableUnits)} нат.ед)`;
              item._stock_balance_packages = availablePkg;
              item._stock_balance_units = availableUnits;
            }
          } catch (error) {
            // Игнорируем ошибки
          }
        }
      }
    };

    // Передаем ссылку на грид в контроллер
    watch(
      itemsGrid,
      (grid: any) => {
        if (grid) {
          controller.setItemsGrid(itemsGrid);
        }
      },
      { immediate: true },
    );

    // Вызываем onAfterLoad после загрузки данных формы
    watch(
      () => (form.value as any)?.store?.state?.loaded,
      async (loaded: boolean) => {
        if (loaded) {
          await nextTick();
          const store = (form.value as any)?.store;
          if (store?.data) {
            const settings = controller.settings();
            if (settings?.onAfterLoad) {
              await settings.onAfterLoad(store);
            }
          }
        }
      },
      { immediate: true },
    );

    // Загружаем остатки при загрузке данных формы
    watch(
      () => (form.value as any)?.data?.items,
      async (items: any[]) => {
        if (items && items.length > 0) {
          await loadStockBalances(items);
          await nextTick();
          const grid = itemsGrid.value as any;
          const gridController = grid?.controller;
          if (gridController?.store?.data?.rows) {
            for (const formItem of items) {
              const gridItem = gridController.store.data.rows.find((r: any) => r.id === formItem.id);
              if (gridItem && formItem._stock_balance) {
                gridItem._stock_balance = formItem._stock_balance;
                gridItem._stock_balance_packages = formItem._stock_balance_packages;
                gridItem._stock_balance_units = formItem._stock_balance_units;
              }
            }
          }
        }
      },
      { immediate: true, deep: true },
    );

    const itemsCount = computed(() => {
      const items = (form.value as any)?.data?.items;
      return Array.isArray(items) ? items.length : 0;
    });

    const itemsConfig = computed(() => ({
      fields: controller.getItemsFields(),
    }));

    const itemsGridConfig = computed(() => ({
      table: "invoice_requirement_items",
      autoLoad: true,
      actions: {
        view: false,
        edit: true,
        create: true,
        copy: false,
        delete: true,
      },
      onBeforeEdit: async (params: any) => {
        const { data } = params;
        if (data.medicament_stock_id) {
          await controller.fillItemFromStock(data.medicament_stock_id, { data });
        }
        params.payload = {
          parentForm: form,
          parentController: controller,
        };
        return edit(params);
      },
      onBeforeNew: async (params: any) => {
        // Автоматически вычисляем следующий position_number
        const items = (form.value as any)?.data?.items || [];
        const maxPos = items.reduce((max: number, item: any) => {
          const pos = parseInt(item.position_number) || 0;
          return pos > max ? pos : max;
        }, 0);
        if (!params.data) params.data = {};
        params.data.position_number = maxPos + 1;
        params.payload = {
          parentForm: form,
          parentController: controller,
        };
        return edit(params);
      },
      onAfterLoad: async (gridController: any) => {
        const items = gridController?.store?.data?.rows || [];
        await loadStockBalances(items);
      },
      fields: controller.getItemsFields(),
    }));

    const issueRequirement = async () => {
      if (form.value) {
        try {
          const saved = await (form.value as any).save();
          if (!saved) {
            Swal.fire({
              title: "Ошибка",
              text: "Не удалось сохранить",
              icon: "error",
              timer: 3000,
              showConfirmButton: false,
            });
            return;
          }
        } catch (error: any) {
          Swal.fire({
            title: "Ошибка",
            text: error.message,
            icon: "error",
            timer: 3000,
            showConfirmButton: false,
          });
          return;
        }
      }

      const result = await Swal.fire({
        title: "Отпустить товары?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Да",
        cancelButtonText: "Нет",
      });

      if (result.isConfirmed) {
        await controller.issue();
      }
    };

    const settings = computed(() => controller.settings());

    // Добавляем кнопку «Сохранить» в футер модального окна (рядом с «Закрыть»)
    onMounted(() => {
      if (props.panel?.setButtons) {
        props.panel.setButtons([
          {
            caption: "Сохранить",
            class: "btn-save",
            onClick: async () => {
              if (form.value) {
                await (form.value as any).save();
              }
            },
          },
        ]);
      }
    });

    return {
      form,
      controller,
      itemsCount,
      itemsConfig,
      itemsGridConfig,
      itemsGrid,
      issueRequirement,
      settings,
    };
  },
});
</script>

<style>
.my-input-1 {
  max-width: 200px;
  min-width: 200px;
}

.bold-label {
  font-weight: 600;
}

.date-input {
  max-width: 170px;
  min-width: 170px;
}

.card-header {
  background-color: #e5e5e5 !important;
}

.card {
  background-color: var(--dv-workspace, #eef2f4);
}

.card-body {
  background-color: #fff;
}

@media (max-width: 430px) {
  .my-input-1,
  .date-input {
    max-width: 100%;
    min-width: 100%;
  }
}
</style>
